import { useCallback, useEffect, useMemo, useState } from 'react'
import ClientDashboard from '../../components/ClientDashboard'
import CreatorOperations from '../../components/CreatorOperations'
import TaskLinks from '../../components/TaskLinks'
import { capabilitiesForRole, getNavigation } from '../../configuration/navigation'
import { translate } from '../../configuration/localization'
import type { Campaign, TenantMembership } from '../../types'
import { Brand, Centered, Metric } from '../../ui/foundation/LegacyPrimitives'
import { applyValidatedTheme, clearCustomTheme } from '../../ui/foundation/themeRuntime'
import { CampaignPanel } from '../campaign/public'
import { RoleUserCustomizationStudio } from '../customization/public'
import { GovernanceSurface } from '../governance/public'
import { DataAiPreferences } from '../privacy/public'
import { platformApi, type AuthUser } from '../platform/public'
import { TenantBootstrap } from './TenantBootstrap'

export function WorkspaceScreen({ user }: { user: AuthUser }) {
  const [memberships, setMemberships] = useState<TenantMembership[]>([])
  const [tenantId, setTenantId] = useState('')
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [selectedCampaignId, setSelectedCampaignId] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const loadMemberships = useCallback(async () => {
    setLoading(true)
    setError('')
    const result = await platformApi.workspace.listMemberships(user.id)
    if (result.error) setError(result.error.message)
    const rows = (result.data ?? []) as unknown as TenantMembership[]
    setMemberships(rows)
    setTenantId((current) => current || rows[0]?.tenant_id || '')
    setLoading(false)
  }, [user.id])

  const loadCampaigns = useCallback(async () => {
    if (!tenantId) return
    setError('')
    const currentMembership = memberships.find((item) => item.tenant_id === tenantId)
    const result = currentMembership?.role === 'client_viewer'
      ? await platformApi.campaign.listForClient(tenantId)
      : await platformApi.campaign.listForOperator(tenantId)

    if (result.error) {
      setError(result.error.message)
      setCampaigns([])
      setSelectedCampaignId('')
      return
    }

    const rows = (result.data ?? []) as Campaign[]
    setCampaigns(rows)
    setSelectedCampaignId((current) => rows.some((campaign) => campaign.id === current) ? current : rows[0]?.id || '')
  }, [memberships, tenantId])

  useEffect(() => { void loadMemberships() }, [loadMemberships])
  useEffect(() => { setSelectedCampaignId(''); void loadCampaigns() }, [loadCampaigns])

  const membership = useMemo(() => memberships.find((item) => item.tenant_id === tenantId), [memberships, tenantId])
  const selectedCampaign = useMemo(() => campaigns.find((item) => item.id === selectedCampaignId), [campaigns, selectedCampaignId])
  const isSystemAdmin = membership?.role === 'system_admin'
  const canOperate = membership?.role !== 'client_viewer'
  const canAdminUsers = membership ? ['agency_admin', 'system_admin'].includes(membership.role) : false
  const navigation = useMemo(() => membership ? getNavigation({ role: membership.role, capabilities: capabilitiesForRole(membership.role) }) : [], [membership])

  useEffect(() => {
    const target = document.documentElement
    clearCustomTheme(target)
    const theme = membership?.tenants.branding?.theme
    if (!theme || typeof theme !== 'object' || Array.isArray(theme)) return
    try {
      applyValidatedTheme(target, theme as Record<string, unknown>)
    } catch {
      clearCustomTheme(target)
    }
    return () => clearCustomTheme(target)
  }, [membership])

  if (loading) return <Centered title={translate('app.loadingWorkspace')} />
  if (memberships.length === 0) return <TenantBootstrap onCreated={loadMemberships} />
  if (!membership) return <Centered title={translate('app.loadingWorkspace')} />

  return <div className="app-shell">
    <aside className="sidebar">
      <Brand inverse />
      <nav>{navigation.map((item, index) => <a key={`${item.id}-${item.labelKey}`} className={index === 0 ? 'active' : ''} href={item.href}>{translate(item.labelKey)}</a>)}</nav>
      <div className="sidebar-foot"><small>{user.email}</small><button onClick={() => void platformApi.auth.signOut()}>{translate('auth.logout')}</button></div>
    </aside>
    <main className="content">
      <header className="topbar">
        <div>
          <p className="eyebrow">TENANT WORKSPACE</p>
          <select aria-label="Workspace" value={tenantId} onChange={(event) => setTenantId(event.target.value)}>{memberships.map((item) => <option key={item.tenant_id} value={item.tenant_id}>{item.tenants.name}</option>)}</select>
        </div>
        <div className="top-actions"><span className="role-chip">{membership.role}</span></div>
      </header>
      {error && <div className="notice error">{error}</div>}

      {isSystemAdmin
        ? <GovernanceSurface tenantId={tenantId} canManageSystem />
        : <section id="overview">
          <div className="section-heading"><div><p className="eyebrow">{canOperate ? 'OPERATIONS FIRST' : 'CLIENT VIEWER'}</p><h1>{canOperate ? 'Campaign control center' : 'Campaign dashboard'}</h1></div><p className="muted">{canOperate ? 'Ưu tiên exception và việc cần xử lý, không ưu tiên vanity chart.' : 'Chỉ hiển thị campaign và aggregate đã được agency whitelist.'}</p></div>
          <div className="metric-grid"><Metric label="Tổng campaign" value={campaigns.length} /><Metric label="Đang chạy" value={campaigns.filter((campaign) => campaign.status === 'active').length} /><Metric label="Bản nháp" value={campaigns.filter((campaign) => campaign.status === 'draft').length} /><Metric label="Hoàn thành" value={campaigns.filter((campaign) => ['completed', 'archived'].includes(campaign.status)).length} /></div>
        </section>}

      <DataAiPreferences tenantId={tenantId} />
      {canAdminUsers && <RoleUserCustomizationStudio tenantId={tenantId} />}

      {!isSystemAdmin && <CampaignPanel tenantId={tenantId} userId={user.id} campaigns={campaigns} selectedCampaignId={selectedCampaignId} canOperate={canOperate} onSelect={setSelectedCampaignId} onReload={loadCampaigns} />}

      {!isSystemAdmin && (canOperate
        ? selectedCampaign
          ? <><CreatorOperations tenantId={tenantId} campaign={selectedCampaign} userId={user.id} /><TaskLinks campaignId={selectedCampaign.id} /></>
          : <section id="operations" className="panel"><h2>Chọn campaign để mở creator operations</h2></section>
        : selectedCampaign
          ? <ClientDashboard campaignId={selectedCampaign.id} />
          : <section id="operations" className="panel"><p className="eyebrow">CLIENT VIEWER</p><h2>Chọn campaign để mở dashboard</h2><p className="muted">Bạn chỉ nhìn thấy campaign đã được agency cấp quyền.</p></section>)}

      {!isSystemAdmin && <section id="reports" className="two-column">
        <div className="panel"><p className="eyebrow">{canOperate ? 'M3 ACTIVE' : 'READ ONLY'}</p><h2>{canOperate ? 'Signed-link submission' : 'Dữ liệu được kiểm soát'}</h2><p className="muted">{canOperate ? 'Mỗi task có thể phát hành link riêng, không lưu plaintext token; creator xác nhận và nộp các phiên bản nội dung trên mobile.' : 'Dashboard không trả về creator contact, fee, internal note, signed link, nội dung submission hoặc review comment.'}</p></div>
        <div className="panel"><p className="eyebrow">AUDITABILITY</p><h2>Kiểm soát thay đổi</h2><ul className="check-list"><li>Tenant isolation bằng RLS</li><li>Campaign access theo từng user</li><li>Client Viewer không có quyền ghi</li><li>Field whitelist được kiểm tra phía server</li><li>Submission versions không ghi đè</li></ul></div>
      </section>}
    </main>
  </div>
}
